import React from 'react';
import { Document, Page, View, Text } from '@react-pdf/renderer';
import { commonStyles, COLORS, formatExamDate, historyToString, calculateVolume, formatListItems, buildCombinedReason, getFacilityDisplayName } from '../utils/pdfHelpers';
import { PdfPageHeader, PdfPatientBox, PdfCallReport, PdfAccentSectionHeader, PdfSectionHeader, PdfLabelValue, PdfMeasurementBox, PdfCommentsBox, PdfFooter } from '../utils/PdfSharedComponents';

export function PediatricAbdomenPDF({ data, facilityInfo }) {
  const reason = buildCombinedReason(data.reason, data.history);

  const appendix = data.appendix || {};
  const pylorus = data.pylorus || {};
  const intussusception = data.intussusception || {};

  const isDiameterAbnormal = appendix.diameter && parseFloat(appendix.diameter) > 6;
  const isWallThicknessAbnormal = pylorus.wallThickness && parseFloat(pylorus.wallThickness) >= 3;
  const isPylorusLengthAbnormal = pylorus.pylorusLength && parseFloat(pylorus.pylorusLength) >= 15;

  const findingsLines = [];
  if (appendix.evaluated) {
    findingsLines.push(`Appendix: ${appendix.status || ''}${appendix.diameter ? ` (${appendix.diameter} mm)` : ''}`);
    if (appendix.appendicolith) findingsLines.push('  Appendicolith: Present');
    if (appendix.periappendicealFluid) findingsLines.push('  Periappendiceal fluid: Present');
  }
  if (pylorus.evaluated) {
    findingsLines.push(`Pylorus: ${pylorus.status || ''}`);
    if (pylorus.wallThickness) findingsLines.push(`  Wall thickness: ${pylorus.wallThickness} mm`);
    if (pylorus.pylorusLength) findingsLines.push(`  Pylorus length: ${pylorus.pylorusLength} mm`);
    if (pylorus.peristalsis) findingsLines.push(`  Peristalsis: ${pylorus.peristalsis}`);
  }
  if (intussusception.evaluated) {
    findingsLines.push(`Intussusception: ${intussusception.status || ''}`);
    if (intussusception.dilatedBowel) findingsLines.push('  Dilated bowel: Present');
    if (intussusception.freeFluid) findingsLines.push('  Free fluid: Present');
  }

  return (
    <Document>
      <Page size="LETTER" style={commonStyles.page}>
        <PdfPageHeader facilityInfo={facilityInfo} examTitle="PEDIATRIC ABDOMEN ULTRASOUND WORKSHEET" />
        <PdfPatientBox
          firstName={data.firstName}
          lastName={data.lastName}
          mrn={data.mrn}
          examDate={data.examDate}
          technologist={data.technologist}
          studentWorksheet={data.studentWorksheet}
          supervisingSonographer={data.supervisingSonographer}
          reason={reason}
        />
        <PdfCallReport show={data.callReport} phoneNumber={data.phoneNumber} />

        <View style={commonStyles.twoCol}>
          <View style={commonStyles.colLeft}>
            <View style={commonStyles.measurementBox}>
              <Text style={[commonStyles.infoBoxText, { fontFamily: 'Helvetica-Bold', marginBottom: 4 }]}>FINDINGS SUMMARY</Text>
              <Text style={commonStyles.infoBoxText}>{findingsLines.join('\n') || 'No findings recorded'}</Text>
            </View>
            <PdfCommentsBox comments={data.comments} />
          </View>

          <View style={commonStyles.colRight}>
            {appendix.evaluated && (
              <>
                <PdfAccentSectionHeader title="APPENDIX" />
                <PdfLabelValue label="Status:" value={appendix.status || ''} isAbnormal={appendix.status && appendix.status !== 'Normal'} />
                {appendix.diameter && (
                  <PdfLabelValue label="Diameter:" value={`${appendix.diameter} mm`} isAbnormal={isDiameterAbnormal} />
                )}
                {appendix.notCompressible !== undefined && appendix.status !== 'Not Visualized' && (
                  <PdfLabelValue label="Compressible:" value={appendix.notCompressible ? 'No' : 'Yes'} isAbnormal={appendix.notCompressible} />
                )}
                {appendix.status !== 'Not Visualized' && (
                  <PdfLabelValue label="Periappendiceal fluid:" value={appendix.periappendicealFluid ? 'Present' : 'None'} isAbnormal={appendix.periappendicealFluid} />
                )}
                {appendix.status !== 'Not Visualized' && (
                  <PdfLabelValue label="Appendicolith:" value={appendix.appendicolith ? 'Present' : 'None'} isAbnormal={appendix.appendicolith} />
                )}
              </>
            )}

            {pylorus.evaluated && (
              <>
                <PdfAccentSectionHeader title="PYLORUS" />
                <PdfLabelValue label="Status:" value={pylorus.status || ''} isAbnormal={pylorus.status && pylorus.status !== 'Normal'} />
                {pylorus.wallThickness && (
                  <PdfLabelValue label="Wall thickness:" value={`${pylorus.wallThickness} mm`} isAbnormal={isWallThicknessAbnormal} />
                )}
                {pylorus.pylorusLength && (
                  <PdfLabelValue label="Pylorus length:" value={`${pylorus.pylorusLength} mm`} isAbnormal={isPylorusLengthAbnormal} />
                )}
                {pylorus.peristalsis && (
                  <PdfLabelValue label="Peristalsis:" value={pylorus.peristalsis} isAbnormal={pylorus.peristalsis !== 'Normal'} />
                )}
              </>
            )}

            {intussusception.evaluated && (
              <>
                <PdfAccentSectionHeader title="INTUSSUSCEPTION" />
                <PdfLabelValue label="Status:" value={intussusception.status || ''} isAbnormal={intussusception.status && intussusception.status !== 'Normal'} />
                <PdfLabelValue label="Dilated bowel:" value={intussusception.dilatedBowel ? 'Present' : 'None'} isAbnormal={intussusception.dilatedBowel} />
                <PdfLabelValue label="Free fluid:" value={intussusception.freeFluid ? 'Present' : 'None'} isAbnormal={intussusception.freeFluid} />
              </>
            )}
          </View>
        </View>

        <PdfFooter facilityInfo={facilityInfo} />
      </Page>
    </Document>
  );
}
